import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import SchemaRoundedIcon from '@mui/icons-material/SchemaRounded';
import axiosInstance from '../utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import ClassificationBanner from './ClassificationBanner';
import RequestClassificationField, {
  CUI_CLASSIFICATION,
  classificationIsCui,
} from './RequestClassificationField';
import SchemaBodyFields from './SchemaBodyFields';

const wizardSurfaceSx = {
  borderRadius: 4,
  border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
  background: (theme) =>
    `linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.08)} 0%, ${alpha(
      theme.palette.background.paper,
      0.96,
    )} 32%, ${alpha(theme.palette.info.light, 0.1)} 100%)`,
  boxShadow: (theme) => `0 18px 48px ${alpha(theme.palette.common.black, 0.12)}`,
};


const metricCardSx = {
  p: 2.5,
  minHeight: 132,
  ...wizardSurfaceSx,
};

const formatRouteModeLabel = (routeMode) => {
  switch (routeMode) {
    case 'global':
      return 'Global';
    case 'internal':
    case 'local':
      return 'Internal';
    case 'preferred_list':
      return 'Preferred List';
    case 'direct':
      return 'Direct Partner';
    default:
      return 'Select routing mode';
  }
};

const toDatetimeLocalValue = (date) => {
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const defaultOfferWindowValue = (hoursFromNow) => {
  const date = new Date();
  date.setHours(date.getHours() + hoursFromNow);
  return toDatetimeLocalValue(date);
};

const normalizeRouteMode = (value) => (value === 'local' ? 'internal' : value);

const RequestWizard = ({
  existingWorkspaceId = '',
  existingWorkspaceTitle = '',
  existingCorrId = '',
  existingThreadTitle = '',
  existingIsCui = false,
  defaultRouteMode = '',
  defaultTargetPartnerId = '',
  onRequestCreated,
} = {}) => {
  const navigate = useNavigate();
  const redirectTimeoutRef = useRef(null);
  const activeWorkspaceId = typeof existingWorkspaceId === 'string'
    ? existingWorkspaceId.trim()
    : '';
  const activeCorrId = typeof existingCorrId === 'string'
    ? existingCorrId.trim()
    : '';
  const isExistingWorkspaceRequest = Boolean(activeWorkspaceId);
  const isExistingThreadRequest = Boolean(activeWorkspaceId && activeCorrId);
  const workspaceTargetLabel = (existingWorkspaceTitle || '').trim() || activeWorkspaceId;
  const threadTargetLabel = (existingThreadTitle || '').trim() || activeCorrId;
  const [needCategories, setNeedCategories] = useState([]);
  const [partnerCatalog, setPartnerCatalog] = useState([]);
  const [preferredPartnerLists, setPreferredPartnerLists] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [schema, setSchema] = useState(null);
  const [routeMode, setRouteMode] = useState(
    normalizeRouteMode(defaultRouteMode) || (isExistingWorkspaceRequest ? 'internal' : 'global'),
  );
  const [selectedPreferredPartnerListId, setSelectedPreferredPartnerListId] = useState('');
  const [selectedTargetPartnerId, setSelectedTargetPartnerId] = useState(defaultTargetPartnerId || '');
  const [workspaceName, setWorkspaceName] = useState('');
  const [classification, setClassification] = useState(existingIsCui ? CUI_CLASSIFICATION : '');
  const [offersAcceptedExpiration, setOffersAcceptedExpiration] = useState(() => defaultOfferWindowValue(24));
  const [offerSelectionExpiration, setOfferSelectionExpiration] = useState(() => defaultOfferWindowValue(72));
  const [formData, setFormData] = useState({});
  const [fileData, setFileData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitState, setSubmitState] = useState({
    phase: 'idle',
    message: '',
    workspaceId: '',
    corrId: '',
    requestId: '',
  });
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => () => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (existingIsCui) {
      setClassification(CUI_CLASSIFICATION);
    }
  }, [existingIsCui]);

  useEffect(() => {
    fetchWizardData();
  }, []);

  const schemaFields = useMemo(
    () => (Array.isArray(schema?.body) ? schema.body : Array.isArray(schema?.fields) ? schema.fields : []),
    [schema],
  );

  const selectedPreferredPartnerList = useMemo(
    () => preferredPartnerLists.find((list) => list.list_id === selectedPreferredPartnerListId) || null,
    [preferredPartnerLists, selectedPreferredPartnerListId],
  );

  const selectedTargetPartner = useMemo(
    () => partnerCatalog.find((partner) => partner.partner_id === selectedTargetPartnerId) || null,
    [partnerCatalog, selectedTargetPartnerId],
  );

  const directPartnerOptions = useMemo(
    () => partnerCatalog.filter((partner) => (
      partner.status === 'active' &&
      (!partner.is_user_member || partner.partner_id === selectedTargetPartnerId)
    )),
    [partnerCatalog, selectedTargetPartnerId],
  );

  const normalizeValueForField = (field, value) => {
    if (field?.type === 'number' || field?.type === 'range') {
      if (value === '' || value === null || typeof value === 'undefined') {
        return value;
      }

      const numericValue = Number(value);
      return Number.isNaN(numericValue) ? value : numericValue;
    }
    if (field?.type === 'key_value_pairs') {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value;
      }
      const text = String(value || '').trim();
      if (!text) {
        return {};
      }
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed;
        }
      } catch (_err) {
        // Fall through to key/value-line parsing.
      }
      const pairs = text.split(/\n|,/).reduce((acc, line) => {
        const match = line.match(/^\s*([^:=]+?)\s*[:=]\s*(.*?)\s*$/);
        if (match && match[1].trim()) {
          acc[match[1].trim()] = match[2].trim();
        }
        return acc;
      }, {});
      return Object.keys(pairs).length ? pairs : value;
    }

    return value;
  };

  const resetSchemaState = () => {
    setSchema(null);
    setWorkspaceName('');
    setClassification(existingIsCui ? CUI_CLASSIFICATION : '');
    setFormData({});
    setFileData({});
    setSubmitState({
      phase: 'idle',
      message: '',
      workspaceId: '',
      corrId: '',
      requestId: '',
    });
    setErrorMessage('');
  };

  const fetchWizardData = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const [categoriesResult, catalogResult, listsResult] = await Promise.allSettled([
        axiosInstance.get('/requests/need-categories'),
        axiosInstance.get('/partners/user/catalog'),
        axiosInstance.get('/partners/user/preferred-partner-lists'),
      ]);

      if (categoriesResult.status !== 'fulfilled') {
        throw categoriesResult.reason;
      }

      setNeedCategories(Array.isArray(categoriesResult.value?.data) ? categoriesResult.value.data : []);
      setPartnerCatalog(
        catalogResult.status === 'fulfilled' && Array.isArray(catalogResult.value?.data?.partners)
          ? catalogResult.value.data.partners
          : [],
      );
      setPreferredPartnerLists(
        listsResult.status === 'fulfilled' && Array.isArray(listsResult.value?.data?.lists)
          ? listsResult.value.data.lists
          : [],
      );
      setSelectedCategory(null);
      resetSchemaState();

      const routingWarnings = [];
      if (catalogResult.status !== 'fulfilled') {
        routingWarnings.push('partner catalog');
      }
      if (listsResult.status !== 'fulfilled') {
        routingWarnings.push('preferred partner lists');
      }
      if (routingWarnings.length) {
        setErrorMessage(`Request routing options loaded partially. We could not load: ${routingWarnings.join(' and ')}.`);
      }
    } catch (err) {
      console.error(err);
      setNeedCategories([]);
      setPartnerCatalog([]);
      setPreferredPartnerLists([]);
      setSelectedCategory(null);
      setErrorMessage('Unable to load active AMX need categories right now.');
    } finally {
      setLoading(false);
    }
  };

  const pullRequestSchema = async () => {
    if (!selectedCategory) {
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const res = await axiosInstance.post('/requests/schema', {
        schema_id: selectedCategory.default_request_schema_id,
        schema_version: selectedCategory.default_request_schema_version,
        version: selectedCategory.default_request_schema_version,
      });
      setSchema(res.data?.schema ?? res.data ?? null);
      setFormData({});
      setFileData({});
    } catch (err) {
      console.error(err);
      setSchema(null);
      setErrorMessage('The request schema could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isMissingRequiredValue = (value, field) => {
    if (field?.type === 'boolean') {
      return value !== true;
    }

    return (
      value === null ||
      typeof value === 'undefined' ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0)
    );
  };

  const validateRequiredFields = () => {
    const nextErrors = {};

    schemaFields.forEach((field) => {
      if (!field.required || !field.name) return;

      const value = field.type === 'file' ? fileData[field.name] : formData[field.name];

      if (isMissingRequiredValue(value, field)) {
        nextErrors[field.name] = `${field.label || field.name} is required.`;
      }
    });

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!selectedCategory || !schema) {
      return;
    }

    if (!validateRequiredFields()) {
      setErrorMessage('Complete the required fields before submitting the request.');
      return;
    }

    if (!classification) {
      setErrorMessage('Select a content classification before submitting the request.');
      return;
    }

    if (routeMode === 'preferred_list' && !selectedPreferredPartnerListId) {
      setErrorMessage('Select a preferred partner list before submitting the request.');
      return;
    }

    if (routeMode === 'direct' && !selectedTargetPartnerId) {
      setErrorMessage('Select a direct partner before submitting the request.');
      return;
    }
    if (!offersAcceptedExpiration || !offerSelectionExpiration) {
      setErrorMessage('Set both offer windows before submitting the request.');
      return;
    }
    if (new Date(offerSelectionExpiration) <= new Date(offersAcceptedExpiration)) {
      setErrorMessage('Offer selection must remain open after the offer acceptance window closes.');
      return;
    }

    const requestContent = { ...formData };
    const fieldsByName = schemaFields.reduce((acc, field) => {
      acc[field.name] = field;
      return acc;
    }, {});

    Object.entries(requestContent).forEach(([fieldName, value]) => {
      requestContent[fieldName] = normalizeValueForField(fieldsByName[fieldName], value);
    });

    Object.entries(fileData).forEach(([fieldName, file]) => {
      requestContent[fieldName] = file.name;
    });

    const workspaceTitle = workspaceName.trim() || `Request for ${selectedCategory.category_display_name}`;
    const workspaceDescription = typeof requestContent.description === 'string'
      ? requestContent.description.trim()
      : '';

    setLoading(true);
    setErrorMessage('');
    setSubmitState({
      phase: 'submitting',
      message: isExistingWorkspaceRequest
        ? isExistingThreadRequest
          ? 'Submitting your request to the thread...'
          : 'Submitting your request to the workspace...'
        : 'Submitting your request and provisioning the workspace...',
      workspaceId: '',
      corrId: '',
      requestId: '',
    });

    try {
      const requestPayload = {
        category_id: selectedCategory.category_id,
        is_cui: classificationIsCui(classification),
        route_mode: routeMode,
        preferred_partner_list_id: routeMode === 'preferred_list' ? selectedPreferredPartnerListId : undefined,
        target_partner_id: routeMode === 'direct' ? selectedTargetPartnerId : undefined,
        request_content: requestContent,
      };
      requestPayload.offers_accepted_expiration = new Date(offersAcceptedExpiration).toISOString();
      requestPayload.offer_selection_expiration = new Date(offerSelectionExpiration).toISOString();

      if (isExistingWorkspaceRequest) {
        requestPayload.workspace_id = activeWorkspaceId;
        if (activeCorrId) {
          requestPayload.corr_id = activeCorrId;
        }
      } else {
        requestPayload.workspace = {
          title: workspaceTitle,
          description: workspaceDescription,
          is_cui: classificationIsCui(classification),
        };
      }

      const response = await axiosInstance.post('/requests/new', requestPayload);

      const workspaceId = response.data?.workspace_id;
      const corrId = response.data?.corr_id || '';
      const requestId = response.data?.request_id || '';

      if (!workspaceId) {
        throw new Error('The request was accepted but no workspace_id was returned.');
      }
      if (!corrId) {
        throw new Error('The request was accepted but no corr_id was returned.');
      }

      const filesToUpload = Object.values(fileData).filter(Boolean);

      if (filesToUpload.length) {
        setSubmitState({
          phase: 'uploading',
          message: `Request submitted. Uploading ${filesToUpload.length} file${filesToUpload.length === 1 ? '' : 's'}...`,
          workspaceId,
          corrId,
          requestId,
        });

        for (const file of filesToUpload) {
          await uploadFileToCorrelation(corrId, file);
        }
      }

      setSubmitState({
        phase: 'redirecting',
        message: filesToUpload.length
          ? isExistingWorkspaceRequest
            ? isExistingThreadRequest
              ? 'Request submitted and files uploaded. Refreshing this thread...'
              : 'Request submitted and files uploaded. Refreshing this workspace...'
            : 'Request submitted and files uploaded. Opening your federated workspace...'
          : isExistingWorkspaceRequest
            ? isExistingThreadRequest
              ? 'Request submitted. Refreshing this thread...'
              : 'Request submitted. Refreshing this workspace...'
            : 'Request submitted. Opening your federated workspace...',
        workspaceId,
        corrId,
        requestId,
      });

      redirectTimeoutRef.current = setTimeout(() => {
        if (onRequestCreated) {
          Promise.resolve(onRequestCreated({ workspaceId, corrId, requestId })).catch((callbackError) => {
            console.error('Request creation callback failed', callbackError);
          });
          return;
        }

        navigate(`/workspaces/${workspaceId}`, {
          state: {
            justCreated: true,
            corr_id: corrId,
            request_id: requestId,
          },
        });
      }, 1100);
    } catch (err) {
      console.error(err);
      setSubmitState({
        phase: 'idle',
        message: '',
        workspaceId: '',
        corrId: '',
        requestId: '',
      });
      setErrorMessage(
        err?.response?.data?.detail ||
          err?.message ||
          'Request submission failed. Please review the form and try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleFileChange = (field, file) => {
    setFormData((prev) => ({ ...prev, [field]: file }));
    setFileData((prev) => ({ ...prev, [field]: file }));
  };

  const uploadFileToCorrelation = async (corrId, file) => {
    const response = await axiosInstance.post(`/requests/files/${encodeURIComponent(corrId)}/upload`, {
      filename: file.name,
    });

    const uploadFormData = new FormData();
    Object.entries(response.data?.fields || {}).forEach(([key, value]) => {
      uploadFormData.append(key, value);
    });
    uploadFormData.append('file', file);

    const uploadResponse = await fetch(response.data.url, {
      method: response.data.method || 'POST',
      body: uploadFormData,
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed for ${file.name} with status ${uploadResponse.status}.`);
    }
  };

  const renderField = (field) => {
    const fieldError = fieldErrors[field.name];

    const commonProps = {
      key: field.name,
      label: field.label,
      fullWidth: true,
      required: field.required,
      error: Boolean(fieldError),
      variant: 'outlined',
      sx: { mb: 2 },
      value: formData[field.name] ?? '',
      onChange: (e) => handleInputChange(field.name, normalizeValueForField(field, e.target.value)),
      helperText: field.description || field.helper_text || ' ',
    };

    switch (field.type) {
      case 'textarea':
        return <TextField {...commonProps} multiline rows={4} />;
      case 'number':
        return <TextField {...commonProps} type="number" inputProps={{ step: 'any', min: field.min }} />;
      case 'date':
        return <TextField {...commonProps} type="date" InputLabelProps={{ shrink: true }} />;
      case 'datetime':
        return <TextField {...commonProps} type="datetime-local" InputLabelProps={{ shrink: true }} />;
      case 'boolean':
        return (
          <Box
            key={field.name}
            sx={{
              mb: 2,
              px: 2,
              py: 1.5,
              borderRadius: 3,
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.8)}`, 
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.58),
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={formData[field.name] || false}
                  onChange={(e) => handleInputChange(field.name, e.target.checked)}
                />
              }
              required={field.required}
              label={field.label}
              sx={{
                        mr: 0,
                        ...(fieldError && {
                          color: (theme) => theme.palette.error.main,
                          '& .MuiFormControlLabel-label': {
                            fontWeight: 700, 
                          },
                        }),
                      }}
            />
          </Box>
        );

      case 'select':
        return (
          <TextField {...commonProps} select>
            {field.options.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </TextField>
        );
      case 'multiselect':
        return (
          <FormControl
            key={field.name}
            fullWidth
            required={field.required}
            error={Boolean(fieldError)}
            sx={{ mb: 2 }}
          >
          <InputLabel id={`label-${field.name}`}>{field.label}</InputLabel>
          <Select
            labelId={`label-${field.name}`}
            multiple
            value={formData[field.name] || []}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            input={<OutlinedInput label={field.label} />}
            renderValue={(selected) => selected.join(', ')}
          >
            {field.options.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Checkbox checked={(formData[field.name] || []).includes(opt)} />
                <ListItemText primary={opt} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{fieldError || field.description || field.helper_text || ' '}</FormHelperText>
        </FormControl>
        );
      case 'file':
        return (
          <Button
            key={field.name}
            variant="outlined"
            component="label"
            fullWidth
            sx={{
              mb: 2,
              py: 1.6,
              justifyContent: 'flex-start',
              borderRadius: 3,
              borderStyle: 'dashed',
            }}
          >
            {formData[field.name]?.name || field.label}
            <input
              type="file"
              hidden
              accept={field.accept || '*'}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleFileChange(field.name, file);
                }
              }}
            />
          </Button>
        );
      case 'email':
        return <TextField {...commonProps} type="email" />;
      case 'url':
        return <TextField {...commonProps} type="url" />;
      case 'range':
        return (
          <Box key={field.name} sx={{ mb: 4, px: 1 }}>
            <Typography gutterBottom sx={{ fontWeight: 600 }}>
              {field.label}
            </Typography>
            <Slider
              value={formData[field.name] || 0}
              onChange={(e, val) => handleInputChange(field.name, val)}
              step={1}
              min={0}
              max={10000}
              valueLabelDisplay="auto"
            />
            {field.description || field.helper_text ? (
              <Typography variant="caption" color="text.secondary">
                {field.description || field.helper_text}
              </Typography>
            ) : null}
          </Box>
        );
      case 'tags':
        return (
          <TextField
            {...commonProps}
            placeholder="Comma-separated tags"
            onChange={(e) =>
              handleInputChange(
                field.name,
                e.target.value
                  .split(',')
                  .map((tag) => tag.trim())
                  .filter(Boolean),
              )
            }
          />
        );
      default:
        return <TextField {...commonProps} type="text" />;
    }
  };

  const summaryItems = [
    ...(isExistingWorkspaceRequest
      ? [
          {
            label: 'Workspace',
            value: workspaceTargetLabel,
            icon: <HubRoundedIcon fontSize="small" />,
          },
        ]
      : []),
    ...(isExistingThreadRequest
      ? [
          {
            label: 'Thread',
            value: threadTargetLabel,
            icon: <SchemaRoundedIcon fontSize="small" />,
          },
        ]
      : []),
    {
      label: 'Category',
      value: selectedCategory?.category_display_name || 'Select an AMX need category',
      icon: <LayersRoundedIcon fontSize="small" />,
    },
    {
      label: 'Routing',
      value: selectedPreferredPartnerList
        ? `${formatRouteModeLabel(routeMode)}: ${selectedPreferredPartnerList.name}`
        : selectedTargetPartner
          ? `${formatRouteModeLabel(routeMode)}: ${selectedTargetPartner.display_name || selectedTargetPartner.partner_code || selectedTargetPartner.partner_id}`
          : formatRouteModeLabel(routeMode),
      icon: <EastRoundedIcon fontSize="small" />,
    },
    {
      label: 'Classification',
      value: classification ? (classificationIsCui(classification) ? 'CUI' : 'UNCLASSIFIED') : 'Select classification',
      icon: <SchemaRoundedIcon fontSize="small" />,
    },
    {
      label: 'Schema',
      value: schemaFields.length ? `${schemaFields.length} fields loaded` : 'No schema loaded',
      icon: <SchemaRoundedIcon fontSize="small" />,
    },
  ];

  const isSchemaReady = !!schema && schemaFields.length > 0;
  const isRoutingReady =
    routeMode === 'preferred_list'
      ? !!selectedPreferredPartnerListId
      : routeMode === 'direct'
        ? !!selectedTargetPartnerId
        : true;
  const canSubmitRequest = isSchemaReady && isRoutingReady && !!classification && (!isExistingWorkspaceRequest || !!activeWorkspaceId);

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 5,
          overflow: 'hidden',
          position: 'relative',
          color: 'common.white',
          background: 'linear-gradient(135deg, #081f3f 0%, #0d3b66 42%, #1f7a8c 100%)',
          boxShadow: '0 24px 60px rgba(6, 24, 44, 0.28)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(circle at 18% 18%, rgba(255,255,255,0.12), transparent 24%), radial-gradient(circle at 82% 26%, rgba(255,255,255,0.16), transparent 20%)',
          }}
        />
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between">
          <Stack spacing={1.5} sx={{ maxWidth: 760 }}>
            <Typography variant="overline" sx={{ letterSpacing: '0.24em', opacity: 0.9 }}>
              REQUEST ORCHESTRATION
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {isExistingThreadRequest
                ? 'Fill the initial request'
                : isExistingWorkspaceRequest
                  ? 'Create a new request'
                  : 'Launch a federated workspace'}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.84 }}>
              {isExistingThreadRequest
                ? `Configure the request, load the active schema, and attach it directly to ${threadTargetLabel}.`
                : isExistingWorkspaceRequest
                  ? `Configure the request, load the active schema, and attach it directly to ${workspaceTargetLabel}.`
                  : 'Configure the request, load the active schema, and hand off directly into the workspace once the request is accepted.'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {summaryItems.map((item) => (
                <Chip
                  key={item.label}
                  icon={item.icon}
                  label={`${item.label}: ${item.value}`}
                  variant="outlined"
                  sx={{ color: 'common.white', borderColor: alpha('#ffffff', 0.34) }}
                />
              ))}
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'row', md: 'column' }} spacing={2} justifyContent="space-between">
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Need Categories
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {needCategories.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Submission State
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {submitState.phase === 'redirecting'
                  ? isExistingWorkspaceRequest
                    ? isExistingThreadRequest
                      ? 'Refreshing thread'
                      : 'Refreshing workspace'
                    : 'Opening workspace'
                  : submitState.phase === 'uploading'
                    ? 'Uploading files'
                  : submitState.phase === 'submitting'
                    ? 'Submitting'
                    : 'Ready'}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {classification ? <ClassificationBanner isCui={classificationIsCui(classification)} /> : null}

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      {submitState.phase === 'redirecting' ? (
        <Paper sx={{ ...wizardSurfaceSx, p: { xs: 3, md: 4 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '18px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                  color: 'success.main',
                }}
              >
                <CheckCircleRoundedIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {isExistingWorkspaceRequest ? 'Request created' : 'Workspace created'}
                </Typography>
                <Typography color="text.secondary">
                  {submitState.message}
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Workspace ID
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-all' }}>
                {submitState.workspaceId}
              </Typography>
              {submitState.corrId ? (
                <Typography variant="caption" color="text.secondary">
                  Correlation ID: {submitState.corrId}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Paper>
      ) : null}

              <Stack spacing={2.5} sx={{ mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Request type
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    Choose the category that best matches what you’re requesting.
                  </Typography>
                </Box>

                <TextField
                  select
                  fullWidth
                  label="Category"
                  value={selectedCategory?.category_display_name || ''}
                  onChange={(e) => {
                    const category =
                      needCategories.find(
                        (cat) => cat.category_display_name === e.target.value,
                      ) || null;
                    setSelectedCategory(category);
                    resetSchemaState();
                  }}
                  disabled={
                    needCategories.length === 0 ||
                    loading ||
                    submitState.phase === 'submitting'
                  }
                  helperText={
                    selectedCategory?.description
                      ? selectedCategory.description
                      : needCategories.length
                        ? 'Select a category to continue.'
                        : 'No categories are available yet.'
                  }
                  InputProps={{
                    sx: {
                      alignItems: 'center',
                      py: 1, // your padding tweak
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      lineHeight: 1.1,
                    },
                  }}
                >
                  {needCategories.map((cat) => (
                    <MenuItem
                      key={cat.category_display_name}
                      value={cat.category_display_name}
                      sx={{ py: 1.0 }}
                    >
                      <ListItemText primary={cat.category_display_name} />
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Who should receive this request?
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Decide how this request will be shared with partners.
                      </Typography>
                    </Box>

                    <TextField
                      select
                      fullWidth
                      label="Routing Mode"
                      value={routeMode}
                      onChange={(event) => {
                        const nextRouteMode = event.target.value;
                        setRouteMode(nextRouteMode);
                        if (nextRouteMode !== 'preferred_list') {
                          setSelectedPreferredPartnerListId('');
                        }
                        if (nextRouteMode !== 'direct') {
                          setSelectedTargetPartnerId('');
                        }
                      }}
                      helperText={
                        routeMode === 'global'
                          ? 'Send this request to all eligible partners.'
                          : routeMode === 'internal'
                            ? 'Send only within your parent partner and business units.'
                            : routeMode === 'preferred_list'
                              ? 'Send to partners in one of your saved lists.'
                              : 'Send directly to a single partner.'
                      }
                    >
                      <MenuItem value="global">Global</MenuItem>
                      <MenuItem value="internal">Internal</MenuItem>
                      <MenuItem value="preferred_list">Preferred List</MenuItem>
                      <MenuItem value="direct">Direct Partner</MenuItem>
                    </TextField>

                    {routeMode === 'preferred_list' ? (
                      <TextField
                        select
                        fullWidth
                        label="Preferred Partner List"
                        value={selectedPreferredPartnerListId}
                        onChange={(event) => setSelectedPreferredPartnerListId(event.target.value)}
                        helperText={
                          preferredPartnerLists.length
                            ? 'Select one of your visible preferred partner lists.'
                            : 'No preferred partner lists are available yet.'
                        }
                      >
                        {preferredPartnerLists.map((list) => (
                          <MenuItem key={list.list_id} value={list.list_id}>
                            {list.name}
                            {list.visibility === 'partner_shared' ? ' • Shared' : ' • Private'}
                            {Array.isArray(list.members) ? ` • ${list.members.length} members` : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : null}

                    {routeMode === 'direct' ? (
                      <TextField
                        select
                        fullWidth
                        label="Target Partner"
                        value={selectedTargetPartnerId}
                        onChange={(event) => setSelectedTargetPartnerId(event.target.value)}
                        helperText={
                          directPartnerOptions.length
                            ? 'Choose a single active partner from the catalog.'
                            : 'No direct partner targets are currently available.'
                        }
                      >
                        {directPartnerOptions.map((partner) => (
                          <MenuItem key={partner.partner_id} value={partner.partner_id}>
                            {partner.display_name || partner.partner_code || partner.partner_id}
                            {partner.partner_code ? ` (${partner.partner_code})` : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : null}
                  </Stack>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"   
              >
                <Button
                  variant="contained"
                  onClick={pullRequestSchema}
                  disabled={!selectedCategory || loading || submitState.phase === 'submitting'}
                >
                  Continue to request details
                </Button>
              </Stack>

              {loading && submitState.phase !== 'submitting' ? (
                <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography color="text.secondary">
                    Synchronizing request configuration...
                  </Typography>
                </Stack>
              ) : null}

              {schema && (
                <Paper
                  sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 4,
                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    bgcolor: (theme) => alpha(theme.palette.background.paper, 0.72),
                  }}
                >
                  <Stack spacing={2.5}>
                    <Box>
                      {/* <Typography variant="body2" color="text.secondary">
                        Need Category
                      </Typography> */}
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        sx={{ mt: 0.5 }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {selectedCategory?.category_display_name}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        {selectedCategory?.description || 'The selected category defines the request content. Routing is configured below.'}
                      </Typography>
                    </Box>


                    {isExistingWorkspaceRequest ? (
                      <Alert severity="info">
                        This request will be attached to {isExistingThreadRequest ? threadTargetLabel : workspaceTargetLabel}.
                      </Alert>
                    ) : (
                      <TextField
                        fullWidth
                        label="Workspace Name"
                        value={workspaceName}
                        onChange={(e) => setWorkspaceName(e.target.value)}
                        placeholder={`Request for ${selectedCategory?.category_display_name || 'selected category'}`}
                        helperText="Optional. If left blank, the workspace will use the default generated name."
                      />
                    )}

                    <RequestClassificationField
                      value={classification}
                      onChange={(event) => setClassification(event.target.value)}
                      disabled={existingIsCui || loading || submitState.phase === 'submitting'}
                      helperText={existingIsCui
                        ? 'This request inherits CUI handling from the existing workspace or thread.'
                        : 'Select whether this request contains Controlled Unclassified Information.'}
                    />

                    {schemaFields.length > 0 ? (
                      <SchemaBodyFields
                        fields={schemaFields}
                        values={formData}
                        fileValues={fileData}
                        errors={fieldErrors}
                        onChange={(field, value) => handleInputChange(
                          field.name,
                          field.type === 'key_value_pairs' ? value : normalizeValueForField(field, value),
                        )}
                        onFileChange={(field, file) => handleFileChange(field.name, file)}
                      />
                    ) : (
                      <Alert severity="info">
                        Schema loaded, but no renderable fields were found.
                      </Alert>
                    )}

                    <Paper
                      sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: 4,
                        border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
                        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.66),
                      }}
                    >
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Offer windows
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                            Set the deadlines for receiving advertisements and selecting one.
                          </Typography>
                        </Box>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            fullWidth
                            label="Offers Accepted Until"
                            type="datetime-local"
                            value={offersAcceptedExpiration}
                            onChange={(event) => setOffersAcceptedExpiration(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            required
                          />
                          <TextField
                            fullWidth
                            label="Offer Selection Until"
                            type="datetime-local"
                            value={offerSelectionExpiration}
                            onChange={(event) => setOfferSelectionExpiration(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            required
                          />
                        </Stack>
                      </Stack>
                    </Paper>

                  </Stack>
                </Paper>
              )}

              {isSchemaReady && (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Final action
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {isExistingWorkspaceRequest
                        ? isExistingThreadRequest
                          ? 'Submit the request to this thread'
                          : 'Submit the request to this workspace'
                        : 'Submit the request and transition into the live workspace'}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={
                      submitState.phase === 'submitting'
                        ? <CircularProgress size={18} color="inherit" />
                        : <EastRoundedIcon />
                    }
                    
                    onClick={handleSubmit}
                    disabled={!canSubmitRequest || loading}
                  >
                    {submitState.phase === 'submitting'
                      ? 'Submitting Request'
                      : isExistingWorkspaceRequest
                        ? 'Create Request'
                        : 'Submit Request'}
                  </Button>
                </Stack>
              )}
            </Stack>
  );
};

export default RequestWizard;
